import { create } from "zustand";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "../lib/supabaseClient";
import { logger } from "../lib/logger";

export type AuthStatus = "unauthenticated" | "loading" | "authenticated";

// Store auth subscription for cleanup
let authSubscription: { unsubscribe: () => void } | null = null;

interface AuthState {
  status: AuthStatus;
  session: Session | null;
  user: User | null;
  error: string | null;
  initializing: boolean;
  isAuthenticated: boolean; // Computed: status === "authenticated"
  setSession: (session: Session | null) => void;
  loadInitialSession: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (email: string, password: string, redirectTo?: string) => Promise<void>;
  signOut: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  status: "unauthenticated",
  session: null,
  user: null,
  error: null,
  initializing: true,
  isAuthenticated: false,

  setSession: (session) => {
    set({
      session,
      user: session?.user ?? null,
      status: session ? "authenticated" : "unauthenticated",
      isAuthenticated: !!session
    });
  },

  loadInitialSession: async () => {
    // Prevent multiple initializations
    if (!get().initializing) {
      logger.warn("⚠️ loadInitialSession called but not initializing, skipping");
      return;
    }

    try {
      const {
        data: { session },
        error
      } = await supabase.auth.getSession();

      if (error) {
        logger.error("❌ Error loading session", error);
        set({ error: error.message, initializing: false });
        return;
      }

      logger.log("✅ Session loaded:", session ? `User ${session.user.id}` : "No session");

      set({
        session,
        user: session?.user ?? null,
        status: session ? "authenticated" : "unauthenticated",
        isAuthenticated: !!session
      });

      // Set initializing to false immediately so UI can render
      // Profile will be loaded via React Query hook in components
      set({ initializing: false });

      // Clean up existing subscription if any
      if (authSubscription) {
        logger.log("🧹 Cleaning up existing auth listener");
        authSubscription.unsubscribe();
        authSubscription = null;
      }

      // Set up auth state change listener
      const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, nextSession) => {
        logger.log("🔄 Auth state changed:", event, nextSession ? `User ${nextSession.user.id}` : "No session");
        
        set({
          session: nextSession,
          user: nextSession?.user ?? null,
          status: nextSession ? "authenticated" : "unauthenticated",
          isAuthenticated: !!nextSession
        });

        // If email was just confirmed, profile will be refetched via React Query
        // No need to manually refresh here

        // If token was refreshed, just log it
        if (event === "TOKEN_REFRESHED") {
          logger.log("✅ Token refreshed");
        }
      });

      // Store subscription for cleanup
      authSubscription = subscription;
    } catch (error) {
      logger.error("💥 Exception in loadInitialSession:", error);
      set({ error: error instanceof Error ? error.message : "Unknown error", initializing: false });
    }
  },

  signInWithEmail: async (email, password) => {
    set({ status: "loading", error: null });
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    if (error) {
      set({ error: error.message, status: "unauthenticated", isAuthenticated: false });
      return;
    }

    set({
      session: data.session,
      user: data.user,
      status: "authenticated",
      isAuthenticated: true,
      error: null
    });
    // Profile will be loaded via React Query hook in components
  },

  signUpWithEmail: async (email, password, redirectTo?: string) => {
    set({ status: "loading", error: null });
    
    // Sign up - Supabase's Send Email Hook automatically sends
    // confirmation email via Resend
    // Pass redirectTo to Supabase so it's included in the email confirmation URL
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: redirectTo ? {
        emailRedirectTo: `${window.location.origin}/auth/confirm?next=${encodeURIComponent(redirectTo)}`,
      } : undefined,
    });

    if (error) {
      set({ error: error.message, status: "unauthenticated", isAuthenticated: false });
      return;
    }

    set({
      session: data.session,
      user: data.user,
      status: data.session ? "authenticated" : "unauthenticated",
      isAuthenticated: !!data.session,
      error: null
    });
  },

  signOut: async () => {
    await supabase.auth.signOut();
    set({
      session: null,
      user: null,
      status: "unauthenticated",
      isAuthenticated: false
    });
  }
}));

// Export cleanup function for potential app-wide cleanup
export const cleanupAuthListener = () => {
  if (authSubscription) {
    logger.log("🧹 Cleaning up auth subscription");
    authSubscription.unsubscribe();
    authSubscription = null;
  }
};
